import { offlineExchange } from '@urql/exchange-graphcache'
import { makeDefaultStorage } from '@urql/exchange-graphcache/default-storage'
import {
  readAllPassesQuery,
  readGuestListForCurrentSeasonQuery,
  readMessagesQuery,
  readOneUserQuery,
  readSectionLeadSlotsByRoleQuery,
  readSectionLeadSlotsPerDateBySectionQuery,
  readSectionSlotsByRoleQuery,
  readSectionSlotsPerDateBySectionQuery,
  readSeasonCheckinItemsAdministrationQuery,
  readSeasonSectionRoleGroupsAdministrationQuery,
  readUserNotesQuery,
  sectionSlotFragment
} from '../queries'

export const cacheDatabaseName = 'beckenrocker-portal-cache'

const storage = makeDefaultStorage({
  idbName: cacheDatabaseName,
  maxAge: 1,
});

const cache = offlineExchange({
  schema: JSON.parse(window.localStorage.getItem('schema')),
  keys: {
    ConcurrentSlotDate: data => data.date,
    ConcurrentSlotTime: data => data.begin,
    User: data => data.id,
    NavigationItem: data => data.href,
    UserWorkloadTime: data => `${data.userID}-${data.date}`,
    UserAvailabilityDuration: data => `${data.userID}-${data.date}`
  },
  resolvers: {
    Query: {
      readOneMembership: (parent, args, cache) => ({
        __typename: 'Membership',
        id: args.input.membershipID,
      }),
      readOneUser: (parent, args, cache) => ({
        __typename: 'User',
        id: args.input.userID,
      }),
      readOneGroup: (parent, args, cache) => ({
        __typename: 'UserGroup',
        id: args.input.groupID,
      }),
      readOneAvailabilitySlot: (parent, args, cache) => ({
        __typename: 'AvailabilitySlot',
        id: args.input.id,
      })
    },
    User: {
      permissions(parent, args, cache, info) {
        return parent.permissions?.split(',') || []
      },
    },
  },
  optimistic: {
    updatePropertyAnswer: (variables, cache, info) => {
      return {
        __typename: 'PropertyAnswer',
        id: variables.input.answerID,
        value: variables.input.value,
      }
    },
    addUserNote: (variables, cache, info) => {
      const myselfKey = cache.resolve('Query', 'readMyself')

      return {
        __typename: 'UserNote',
        id: 'unknown',
        author: myselfKey,
        created: new Date().toLocaleString('sv'),
        message: variables.input.message,
      }
    },
    changeStatusLabelForMembership: (args, cache, info) => {
      const statusLabels = cache.resolve(`Membership:${args.input.membershipID}`, 'statusLabels') || []

      return {
        __typename: 'Membership',
        id: args.input.membershipID,
        statusLabels: args.input.active
          ? [...statusLabels, `StatusLabel:${args.input.statusLabelID}`].filter((value, index, array) => array.indexOf(value) === index)
          : statusLabels.filter(item => item !== `StatusLabel:${args.input.statusLabelID}`)
      }
    },
    updateScheduleSlot: (args, cache, info) => {
      const begin = cache.resolve(`SectionSlot:${args.input.scheduleSlotID}`, 'begin') || ''
      const date = begin.slice(0, 10)

      return {
        __typename: 'SectionSlot',
        id: args.input.scheduleSlotID,
        begin: `${date} ${args.input.begin}:00`,
        end: `${date} ${args.input.end}:00`
      }
    },
    updateLeadScheduleSlot: (args, cache, info) => {
      const begin = cache.resolve(`SectionSlotLead:${args.input.leadScheduleSlotID}`, 'begin') || ''
      const date = begin.slice(0, 10)

      const fistName = args.input.acceptedUserID ? cache.resolve(`User:${args.input.acceptedUserID}`, 'fistName') : ''
      const surname = args.input.acceptedUserID ? cache.resolve(`User:${args.input.acceptedUserID}`, 'surname') : ''

      return {
        __typename: 'SectionSlotLead',
        id: args.input.leadScheduleSlotID,
        ...(args.input.begin ? {begin: `${date} ${args.input.begin}:00`} : {}),
        ...(args.input.end ? {end: `${date} ${args.input.end}:00`} : {}),
        ...(args.input.acceptedUserID ? {
          acceptedUser: {
            __typename: 'User',
            id: args.input.acceptedUserID,
            fistName,
            surname
          }
        } : {}),
      }
    },
    acceptUserForSectionSlot: (args, cache, info) => {
      const acceptedSlots= cache.resolve(`User:${args.input.userID}`, 'acceptedSlots') || []
      return {
        __typename: 'User',
        id: args.input.userID,
        acceptedSlots: [
          ...acceptedSlots,
          {
            __typename: 'SectionSlot',
            id: args.input.sectionSlotID,
            acceptedUser: {
              __typename: 'User',
              id: args.input.userID,
            }
          }
        ]
      }
    },
    dropUserForSectionSlot: (args, cache, info) => {
      const acceptedSlots= cache.resolve(`User:${args.input.userID}`, 'acceptedSlots') || []
      console.log(acceptedSlots)
      return {
        __typename: 'User',
        id: args.input.userID,
        acceptedSlots: acceptedSlots.filter(slot => slot !== `SectionSlot:${args.input.sectionSlotID}`)
      }
    },
    transferUserForSectionSlot: (args, cache, info) => {
      return {
        __typename: 'SectionSlot',
        id: args.input.sectionSlotID,
        acceptedUser: {
          __typename: 'User',
          id: args.input.userID
        }
      }
    },
    updateStatusLabel: (args, cache, info) => {
      return {
        __typename: 'StatusLabel',
        id: args.input.statusLabelID,
        ...(args.input.title ? {title: args.input.title} : {}),
        ...(args.input.color ? {color: args.input.color} : {})
      }
    },
    checkinUser: (args, cache, info) => {
      if (args.input.checkinItemID) {
        const list = cache.resolve(`SeasonCheckin:${args.input.seasonCheckinID}`, 'seasonCheckinItems')

        console.log(list)
        const wasIncluded = (list || []).findIndex(item => item.includes(args.input.checkinItemID)) >= 0

        console.log('was included ', wasIncluded ? '' : [...list, `SeasonCheckinItem:${args.input.seasonCheckinID}`])

        return {
          __typename: 'SeasonCheckin',
          id: args.input.seasonCheckinID,
          seasonCheckinItems: wasIncluded
            ? list.filter(item => !item.includes(args.input.checkinItemID))
            : [
              ...list,
              {
                __typename: 'SeasonCheckinItem',
                id: args.input.checkinItemID,
                checkinTime: `${new Date().toISOString().slice(0, 10)} ${new Date().toLocaleTimeString('de', {hour: '2-digit', minute: '2-digit', second: '2-digit'})}`
              }
            ]
        }
      }

      return {
        __typename: 'SeasonCheckin',
        id: args.input.seasonCheckinID,
        checkinTime: !cache.resolve(`SeasonCheckin:${args.input.seasonCheckinID}`, 'checkinTime')
          ? `${new Date().toISOString().slice(0, 10)} ${new Date().toLocaleTimeString('de', {hour: '2-digit', minute: '2-digit', second: '2-digit'})}`
          : null
      }
    }
  },
  updates: {
    Mutation: {
      // Alle Daten gehören zum bisherigen Festival und müssen neu geladen werden
      selectFestival(_result, _args, cache, _info) {
        cache.inspectFields('Query').forEach(field => {
          cache.invalidate('Query', field.fieldName, field.arguments)
        })
      },
      createSeasonCheckinItemPropertyOptionQuantity(result, args, cache, _info) {
        if (result.createSeasonCheckinItemPropertyOptionQuantity) {
          cache.updateQuery({
            query: readSeasonCheckinItemsAdministrationQuery,
          }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                seasonCheckinItems: (data.readCurrentSeason.seasonCheckinItems || []).map(item => {
                  if (item?.id !== args.input.seasonCheckinItemID) {
                    return item
                  }

                  return {
                    ...item,
                    propertyOptionQuantities: result.createSeasonCheckinItemPropertyOptionQuantity
                  }
                })
              }
            }
          });
        }
      },
      createSectionRoleGroup(result, args, cache, _info) {
        if (result.createSectionRoleGroup) {
          cache.updateQuery({
            query: readSeasonSectionRoleGroupsAdministrationQuery,
          }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                sectionRoleGroups: result.createSectionRoleGroup
              }
            }
          });
        }
      },
      updateSectionRoleGroup(result, args, cache, _info) {
        if (result.updateSectionRoleGroup) {
          cache.updateQuery({
            query: readSeasonSectionRoleGroupsAdministrationQuery,
          }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                sectionRoleGroups: result.updateSectionRoleGroup
              }
            }
          });
        }
      },
      deleteSectionRoleGroup(result, args, cache, _info) {
        if (result.deleteSectionRoleGroup) {
          cache.updateQuery({
            query: readSeasonSectionRoleGroupsAdministrationQuery,
          }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                sectionRoleGroups: result.deleteSectionRoleGroup
              }
            }
          });
        }
      },
      updateSeasonCheckinItemPropertyOptionQuantity(result, args, cache, _info) {
        if (result.updateSeasonCheckinItemPropertyOptionQuantity) {
          const seasonCheckinItemRef = args.input.seasonCheckinItemID
            ? `SeasonCheckinItem:${args.input.seasonCheckinItemID}`
            : cache.resolve(`SeasonCheckinItemPropertyOptionQuantity:${args.input.id}`, 'seasonCheckinItem')

          const seasonCheckinItemID = seasonCheckinItemRef
            ? cache.resolve(seasonCheckinItemRef, 'id')
            : null

          if (!seasonCheckinItemID) {
            return
          }

          cache.updateQuery({
            query: readSeasonCheckinItemsAdministrationQuery,
          }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                seasonCheckinItems: (data.readCurrentSeason.seasonCheckinItems || []).map(item => {
                  if (item?.id !== seasonCheckinItemID) {
                    return item
                  }

                  return {
                    ...item,
                    propertyOptionQuantities: result.updateSeasonCheckinItemPropertyOptionQuantity
                  }
                })
              }
            }
          });
        }
      },
      deleteSeasonCheckinItemPropertyOptionQuantity(result, args, cache, _info) {
        if (result.deleteSeasonCheckinItemPropertyOptionQuantity) {
          const seasonCheckinItemRef = cache.resolve(`SeasonCheckinItemPropertyOptionQuantity:${args.input.id}`, 'seasonCheckinItem')
          const seasonCheckinItemID = seasonCheckinItemRef ? cache.resolve(seasonCheckinItemRef, 'id') : null

          if (!seasonCheckinItemID) {
            return
          }

          cache.updateQuery({
            query: readSeasonCheckinItemsAdministrationQuery,
          }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                seasonCheckinItems: (data.readCurrentSeason.seasonCheckinItems || []).map(item => {
                  if (item?.id !== seasonCheckinItemID) {
                    return item
                  }

                  return {
                    ...item,
                    propertyOptionQuantities: result.deleteSeasonCheckinItemPropertyOptionQuantity
                  }
                })
              }
            }
          });
        }
      },
      addUserNote(result, args, cache, _info) {
        cache.updateQuery({ query: readUserNotesQuery, variables: { input: { userID: args.input.userID }} }, data => {
          if (!result.addUserNote) {
            return {
              readUserNotes: [
                ...data.readUserNotes.filter(item => item !== null),
              ]
            }
          }

          return {
            readUserNotes: [
              ...data.readUserNotes.filter(item => item !== null),
              result.addUserNote
            ]
          }
        });

        cache.updateQuery({ query: readOneUserQuery, variables: { input: { userID: args.input.userID }} }, data => {
          return {
            readOneUser: {
              ...(data?.readOneUser || {}),
              userNoteAmount: cache.resolve('Query', 'readUserNotes', { input: { userID: args.input.userID }}).length
            }
          }
        })
      },
      acceptUserForSectionSlotAndDuplicate(result, args, cache, _info) {
        cache.updateQuery({ query: readSectionSlotsPerDateBySectionQuery, variables: { input: { sectionID: args.input.sectionID, date: result.acceptUserForSectionSlotAndDuplicate.begin.slice(0, 10) } } }, data => {
          return {
            readSectionSlotsPerDateBySection: [
              ...data.readSectionSlotsPerDateBySection,
              result.acceptUserForSectionSlotAndDuplicate
            ]
          }
        });
      },
      addPass(result, args, cache, _info) {
        cache.updateQuery({ query: readAllPassesQuery }, data => {
          return {
            readAllPasses: [
              ...data.readAllPasses.filter(item => item !== null),
              result.addPass
            ]
          }
        });
      },
      createSeasonCheckinItem(result, args, cache, _info) {
        if (result.createSeasonCheckinItem) {
          cache.updateQuery({ query: readSeasonCheckinItemsAdministrationQuery }, data => {
            return {
              readCurrentSeason: {
                ...(data?.readCurrentSeason || {}),
                seasonCheckinItems: result.createSeasonCheckinItem
              }
            }
          });
        }
      },
      deleteSeasonCheckinItem(result, args, cache, _info) {
        if (result.deleteSeasonCheckinItem) {
          cache.updateQuery({ query: readSeasonCheckinItemsAdministrationQuery }, data => {
            return {
              readCurrentSeason: {
                ...(data?.readCurrentSeason || {}),
                seasonCheckinItems: result.deleteSeasonCheckinItem
              }
            }
          });
        }
      },
      createSeasonCheckinItemGenericIssue(result, args, cache, _info) {
        if (result.createSeasonCheckinItemGenericIssue) {
          cache.updateQuery({ query: readSeasonCheckinItemsAdministrationQuery }, data => {
            return {
              readCurrentSeason: {
                ...(data?.readCurrentSeason || {}),
                ...result.createSeasonCheckinItemGenericIssue
              }
            }
          })
        }
      },
      updateSeasonCheckinItemGenericIssue(result, args, cache, _info) {
        if (result.updateSeasonCheckinItemGenericIssue) {
          cache.updateQuery({ query: readSeasonCheckinItemsAdministrationQuery }, data => ({
            readCurrentSeason: {
              ...(data?.readCurrentSeason || {}),
              ...result.updateSeasonCheckinItemGenericIssue
            }
          }))
        }
      },
      deleteSeasonCheckinItemGenericIssue(result, args, cache, _info) {
        if (result.deleteSeasonCheckinItemGenericIssue) {
          cache.updateQuery({ query: readSeasonCheckinItemsAdministrationQuery }, data => ({
            readCurrentSeason: {
              ...(data?.readCurrentSeason || {}),
              ...result.deleteSeasonCheckinItemGenericIssue
            }
          }))
        }
      },
      updateCurrentSeasonCheckinSettings(result, args, cache, _info) {
        if (result.updateCurrentSeasonCheckinSettings) {
          cache.updateQuery({ query: readSeasonCheckinItemsAdministrationQuery }, data => {
            if (!data?.readCurrentSeason) {
              return data
            }

            return {
              readCurrentSeason: {
                ...(data.readCurrentSeason || {}),
                ...result.updateCurrentSeasonCheckinSettings,
              }
            }
          });
        }
      },
      deletePass(result, args, cache, _info) {
        cache.updateQuery({ query: readAllPassesQuery }, data => {
          return {
            readAllPasses: [
              ...result.deletePass
            ]
          }
        });
      },
      addScheduleSlotToRole(result, args, cache, _info) {
        if (result.addScheduleSlotToRole) {
          let date = args.input.date;
          const isoDate = date.split(".").reverse().join("-");

          cache.updateQuery({
            query: readSectionSlotsPerDateBySectionQuery,
            variables: {input: {sectionID: args.input.sectionID, date: isoDate}}
          }, () => {
            return {
              readSectionSlotsPerDateBySection: [
                ...result.addScheduleSlotToRole
              ]
            }
          });
        }
      },
      addMessage(result, args, cache, _info) {
        if (result.addMessage) {
          cache.updateQuery({
            query: readMessagesQuery,
          }, () => {
            return {
              readMessages: [
                ...result.addMessage
              ]
            }
          });
        }
      },
      deleteMessage(result, args, cache, _info) {
        if (result.deleteMessage) {
          cache.updateQuery({
            query: readMessagesQuery,
          }, () => {
            return {
              readMessages: [
                ...result.deleteMessage
              ]
            }
          });
        }
      },
      removeScheduleSlotFromRole(result, args, cache, _info) {
        if (result.removeScheduleSlotFromRole) {
          let date = args.input.date;
          let sectionID = args.input.sectionID

          cache.updateQuery({
            query: readSectionSlotsPerDateBySectionQuery,
            variables: {input: {date, sectionID}}
          }, () => {
            return {
              readSectionSlotsPerDateBySection: [
                ...result.removeScheduleSlotFromRole
              ]
            }
          });
        }
      },
    removeScheduleSlotLeadFromRole(result, args, cache, _info) {
      if (result.removeScheduleSlotLeadFromRole) {
        let date = args.input.date;
        let roleID = args.input.roleID

        const slotLeadRef = cache.resolve(`SectionSlotLead:${args.input.slotLeadID}`, 'sectionRole')
        const sectionRef = slotLeadRef ? cache.resolve(slotLeadRef, 'section') : null
        const sectionID = sectionRef ? cache.resolve(sectionRef, 'id') : undefined
          || (() => {
            const fallbackSectionRef = cache.resolve(`SectionRole:${roleID}`, 'section')
            return fallbackSectionRef ? cache.resolve(fallbackSectionRef, 'id') : undefined
          })()

        cache.updateQuery({
          query: readSectionLeadSlotsByRoleQuery,
          variables: {input: {date, roleID}}
        }, () => {
          return {
            readSectionLeadSlotsByRole: [
              ...result.removeScheduleSlotLeadFromRole
            ]
          }
        });

        if (sectionID) {
          cache.updateQuery({
            query: readSectionLeadSlotsPerDateBySectionQuery,
            variables: {input: {date, sectionID}}
          }, () => {
            return {
              readSectionLeadSlotsPerDateBySection: [
                ...result.removeScheduleSlotLeadFromRole
              ]
            }
          });
        }
      }
    },
      createGuestListItem(result, args, cache, _info) {
        if (result.createGuestListItem) {
          cache.updateQuery({query: readGuestListForCurrentSeasonQuery, variables: {'input': {'type': args.input.type}}}, () => {
            return {
              readGuestListForCurrentSeason: [
                ...cache.resolve('Query', 'readGuestListForCurrentSeason', {'input': {'type': args.input.type}}),
                result.createGuestListItem
              ].sort((a, b) => {
                const aSurname = (cache.resolve(a, 'surname') || '').toLowerCase()
                const bSurname = (cache.resolve(b, 'surname') || '').toLowerCase()
                const cmp = aSurname.localeCompare(bSurname)

                if (cmp === 0) {
                  const aFirstname = (cache.resolve(a, 'firstName') || '').toLowerCase()
                  const bFirstname = (cache.resolve(b, 'firstName') || '').toLowerCase()
                  return aFirstname.localeCompare(bFirstname)
                }

                return cmp
              })
            }
          });
        }
      },
      deleteGuestListItem(result, args, cache, _info) {
        if (result.deleteGuestListItem) {
          cache.updateQuery({query: readGuestListForCurrentSeasonQuery, variables: {'input': {'type': args.input.type}}}, () => {
            return {
              readGuestListForCurrentSeason: [
                ...result.deleteGuestListItem
              ]
            }
          });
        }
      },
      splitScheduleSlot(result, args, cache, _info) {
        if (result.splitScheduleSlot) {
          cache.updateQuery({query: readSectionSlotsByRoleQuery, variables: {input: { date: args.input.date, roleID: args.input.roleID }}}, () => {
            return {
              readSectionSlotsByRole: [
                ...result.splitScheduleSlot
              ]
            }
          });

          const slotRef = cache.resolve(`SectionSlot:${args.input.scheduleSlotID}`, 'sectionRole')
          const sectionRef = slotRef ? cache.resolve(slotRef, 'section') : null
          const sectionID = sectionRef ? cache.resolve(sectionRef, 'id') : undefined

          if (sectionID) {
            cache.updateQuery({
              query: readSectionSlotsPerDateBySectionQuery,
              variables: {input: {date: args.input.date, sectionID}}
            }, () => {
              return {
                readSectionSlotsPerDateBySection: [
                  ...result.splitScheduleSlot
                ]
              }
            });
          }
        }
      },
      duplicateScheduleSlot(result, args, cache, _info) {
        const slotID = args.input.scheduleSlotID
        const begin = cache.resolve(`SectionSlot:${slotID}`, 'begin')

        if (result.duplicateScheduleSlot) {
          cache.updateQuery({
            query: readSectionSlotsPerDateBySectionQuery,
            variables: { input: { date: begin.slice(0, 10), sectionID: args.input.sectionID } }
          }, () => {
            return {
              readSectionSlotsPerDateBySection: [
                ...result.duplicateScheduleSlot
              ]
            }
          });
        }
      },
      convertScheduleSlotToLead(result, args, cache, _info) {
        const slotID = args.input.sectionSlotID
        const slotBegin = cache.resolve(`SectionSlot:${slotID}`, 'begin')
        const slotSectionRef = cache.resolve(`SectionSlot:${slotID}`, 'sectionRole')
        const sectionRef = slotSectionRef ? cache.resolve(slotSectionRef, 'section') : null

        const sectionID = sectionRef ? cache.resolve(sectionRef, 'id') : (
          result.convertScheduleSlotToLead?.find(item => item?.sectionRole?.section?.id)?.sectionRole?.section?.id
        )

        const date = (slotBegin || result.convertScheduleSlotToLead?.find(item => item?.begin)?.begin || '').slice(0, 10)

        if (date && sectionID) {
          cache.updateQuery({
            query: readSectionSlotsPerDateBySectionQuery,
            variables: { input: { date, sectionID } }
          }, data => {
            return {
              readSectionSlotsPerDateBySection: [
                ...(data?.readSectionSlotsPerDateBySection || []).filter(slot => slot?.id !== slotID)
              ]
            }
          });

          cache.updateQuery({
            query: readSectionLeadSlotsPerDateBySectionQuery,
            variables: { input: { date, sectionID } }
          }, () => {
            return {
              readSectionLeadSlotsPerDateBySection: [
                ...(result.convertScheduleSlotToLead || []).filter(Boolean)
              ]
            }
          });
        }
      },
      convertScheduleSlotLeadToSlot(result, args, cache, _info) {
        const slotLeadID = args.input.leadScheduleSlotID
        const slotLeadBegin = cache.resolve(`SectionSlotLead:${slotLeadID}`, 'begin')
        const slotLeadSectionRef = cache.resolve(`SectionSlotLead:${slotLeadID}`, 'sectionRole')
        const sectionRef = slotLeadSectionRef ? cache.resolve(slotLeadSectionRef, 'section') : null

        const sectionID = sectionRef ? cache.resolve(sectionRef, 'id') : (
          result.convertScheduleSlotLeadToSlot?.find(item => item?.sectionRole?.section?.id)?.sectionRole?.section?.id
        )

        const date = (slotLeadBegin || result.convertScheduleSlotLeadToSlot?.find(item => item?.begin)?.begin || '').slice(0, 10)

        if (date && sectionID) {
          cache.updateQuery({
            query: readSectionLeadSlotsPerDateBySectionQuery,
            variables: { input: { date, sectionID } }
          }, data => {
            return {
              readSectionLeadSlotsPerDateBySection: [
                ...(data?.readSectionLeadSlotsPerDateBySection || []).filter(slot => slot?.id !== slotLeadID)
              ]
            }
          });

          cache.updateQuery({
            query: readSectionSlotsPerDateBySectionQuery,
            variables: { input: { date, sectionID } }
          }, () => {
            return {
              readSectionSlotsPerDateBySection: [
                ...(result.convertScheduleSlotLeadToSlot || []).filter(Boolean)
              ]
            }
          });
        }
      },
      addScheduleSlotLeadToRole(result, args, cache, _info) {
        if (result.addScheduleSlotLeadToRole) {
          let date = args.input.date;
          const isoDate = date.split(".").reverse().join("-");

          cache.updateQuery({
            query: readSectionLeadSlotsByRoleQuery,
            variables: {input: {roleID: args.input.roleID, date: isoDate}}
          }, () => {
            return {
              readSectionLeadSlotsByRole: [
                ...result.addScheduleSlotLeadToRole
              ]
            }
          });

          cache.updateQuery({
            query: readSectionLeadSlotsPerDateBySectionQuery,
            variables: {input: {date: isoDate, sectionID: args.input.sectionID}}
          }, () => {
            return {
              readSectionLeadSlotsPerDateBySection: [
                ...result.addScheduleSlotLeadToRole
              ]
            }
          });
        }
      },
      dropUserForSectionSlot(result, args, cache, _info) {
        if (result.dropUserForSectionSlot) {
          cache.writeFragment(
            sectionSlotFragment,
            {
              id: args.input.sectionSlotID,
              acceptedUser: null
            }
          );
        }
      }
      ,
      transferUserForSectionSlot(result, args, cache, _info) {
        if (result.transferUserForSectionSlot) {
          cache.writeFragment(
            sectionSlotFragment,
            {
              id: args.input.sectionSlotID,
              acceptedUser: {
                __typename: 'User',
                id: args.input.userID
              }
            }
          );

          cache.updateQuery({
            query: readOneUserQuery,
            variables: { input: { userID: args.input.currentUserID } }
          }, data => {
            if (!data?.readOneUser) {
              return data
            }

            return {
              readOneUser: {
                ...(data.readOneUser || {}),
                acceptedSlots: (data.readOneUser.acceptedSlots || []).filter(slot => slot?.id !== args.input.sectionSlotID)
              }
            }
          });

          cache.updateQuery({
            query: readOneUserQuery,
            variables: { input: { userID: args.input.userID } }
          }, data => {
            if (!data?.readOneUser) {
              return data
            }

            const existingSlots = (data.readOneUser.acceptedSlots || []).filter(slot => slot?.id !== args.input.sectionSlotID)

            return {
              readOneUser: {
                ...(data.readOneUser || {}),
                acceptedSlots: [
                  ...existingSlots,
                  result.transferUserForSectionSlot
                ]
              }
            }
          });
        }
      }
    },
  },
  storage
});

export default cache;
